(() => {
  'use strict';
  if (customElements.get('arclo-style-inspiration')) return;
  let active = null;
  class ArcloStyleInspiration extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.positions = new WeakMap();
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-asi-open]');
        if (trigger && this.contains(trigger)) this.playCard(trigger);
      }, options);
      this.addEventListener('keydown', event => {
        if (event.target.matches('.asi__inline-video') && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          if (event.target.paused) event.target.play().catch(() => {});
          else event.target.pause();
        }
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && active?.owner === this) active.video.pause();
      }, options);
      window.addEventListener('pagehide', () => this.stop(), options);
      document.addEventListener('shopify:section:unload', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.stop();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.hasAttribute('data-design-mode')) return;
        const card = [...this.querySelectorAll('[data-block-id]')].find(el => el.dataset.blockId === event.detail?.blockId);
        if (card) {
          const rail = this.querySelector('.asi__rail');
          rail.scrollLeft += card.getBoundingClientRect().left - rail.getBoundingClientRect().left;
        }
      }, options);
      if ('IntersectionObserver' in window) {
        this.visibility = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.intersectionRatio < .15 && active?.owner === this && active.media === entry.target) active.video.pause();
          });
        }, { threshold: [0, .15] });
      }
    }
    playCard(trigger) {
      if (!trigger.dataset.source) return;
      if (this.dataset.playback === 'modal') { this.playModal(trigger); return; }
      if (active) active.owner.stop();
      const media = trigger.closest('.asi__media');
      const status = media.querySelector('.asi__inline-status');
      status.hidden = true;
      status.textContent = '';
      const video = document.createElement('video');
      video.className = 'asi__inline-video';
      video.controls = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.muted = true;
      video.preload = 'none';
      video.tabIndex = 0;
      video.setAttribute('aria-label', trigger.dataset.videoTitle || 'Style video');
      if (trigger.dataset.poster) video.poster = trigger.dataset.poster;
      const current = { owner: this, video, trigger, media, status };
      active = current;
      const savedTime = this.positions.get(trigger) || 0;
      video.addEventListener('loadedmetadata', () => {
        if (savedTime > 0 && Number.isFinite(video.duration) && savedTime < video.duration) video.currentTime = savedTime;
      }, { once: true });
      video.addEventListener('playing', () => {
        if (active !== current) { video.pause(); return; }
        status.hidden = true;
      });
      video.addEventListener('error', () => {
        if (active !== current) return;
        this.stop();
        status.textContent = 'Video could not load. Tap the cover to try again.';
        status.hidden = false;
        trigger.focus({ preventScroll: true });
      });
      media.append(video);
      trigger.hidden = true;
      video.src = trigger.dataset.source;
      this.visibility?.observe(media);
      video.focus({ preventScroll: true });
      video.play().catch(error => {
        if (active === current && error.name !== 'AbortError') {
          status.textContent = 'Use the play control to start this video.';
          status.hidden = false;
        }
      });
    }
    playModal(trigger) {
      if (active) active.owner.stop();
      if (!this.dialog) {
        this.dialog = document.createElement('dialog');
        this.dialog.className = 'asi__dialog';
        this.dialog.setAttribute('aria-label', 'Style video');
        this.dialog.innerHTML = '<button type="button" class="asi__close" aria-label="Close video">×</button><div class="asi__modal-media"></div><div class="asi__modal-product" hidden><span></span><a>VIEW PRODUCT ↗</a></div>';
        this.append(this.dialog);
        this.dialog.querySelector('.asi__close').addEventListener('click', () => this.stop());
        this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.stop(); });
        this.dialog.addEventListener('keydown', event => { if(event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.stop(); } });
        this.dialog.addEventListener('click', event => { if (event.target === this.dialog) { const r = this.dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.stop(); } });
      }
      const video = document.createElement('video');
      video.className = 'asi__video'; video.controls = true; video.playsInline = true; video.muted = true; video.preload = 'none';
      if (trigger.dataset.poster) video.poster = trigger.dataset.poster;
      video.setAttribute('aria-label', trigger.dataset.videoTitle || 'Style video');
      this.dialog.querySelector('.asi__modal-media').append(video);
      const product = this.dialog.querySelector('.asi__modal-product');
      product.hidden = !trigger.dataset.productUrl;
      product.querySelector('span').textContent = trigger.dataset.productName || '';
      product.querySelector('a').href = trigger.dataset.productUrl || '#';
      active = {owner:this,video,trigger,modal:true};
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      video.src = trigger.dataset.source;
      video.play().catch(() => {});
      this.dialog.querySelector('.asi__close').focus();
    }
    stop() {
      if (active?.owner !== this) return;
      const { video, trigger, media, status, modal } = active;
      active = null;
      this.positions.set(trigger, video.ended ? 0 : video.currentTime);
      video.pause();
      video.removeAttribute('src');
      video.removeAttribute('poster');
      video.load();
      video.remove();
      if (modal) {
        this.dialog.close();
        document.documentElement.style.overflow = this.previousOverflow || '';
        trigger.focus({preventScroll:true});
        return;
      }
      trigger.hidden = false;
      status.hidden = true;
      this.visibility?.unobserve(media);
    }
    disconnectedCallback() {
      this.stop();
      this.visibility?.disconnect();
      this.events?.abort();
      this.events = null;
    }
  }
  customElements.define('arclo-style-inspiration', ArcloStyleInspiration);
})();
